import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { QRCodeCanvas } from "qrcode.react";
import { ArrowLeft, Download, Share2, Printer, CheckCircle2, ShieldCheck, Receipt as ReceiptIcon, Phone, Mail, Globe, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { memberApi } from "@/lib/memberClient";
import { getGuestToken } from "@/lib/guestToken";
import { useMember } from "@/contexts/MemberContext";
import { BRAND } from "@/constants/brand";

export default function PayScratchReceipt() {
  const { paymentId } = useParams();
  const { member } = useMember();
  const [payment, setPayment] = useState(null);
  const [scratchCard, setScratchCard] = useState(null);
  const [err, setErr] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    if (member === null) return; // Wait for MemberContext to resolve (null=loading)
    setErr(null);
    (async () => {
      try {
        if (member) {
          const { data } = await memberApi.get(`/member/payments/${paymentId}`);
          setPayment(data.payment);
          setScratchCard(data.scratch_card);
        } else {
          const gt = getGuestToken();
          const { data } = await api.get(`/pay-scratch/payment/${paymentId}?guest_token=${gt}`);
          setPayment(data.payment);
          setScratchCard(data.scratch_card);
        }
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
    // eslint-disable-next-line
  }, [paymentId, member]);

  const buildPdf = () => {
    if (!payment) return null;
    const doc = new jsPDF({ unit: "mm", format: "a5" });
    const paidAt = payment.paid_at ? new Date(payment.paid_at) : new Date();

    // Header
    doc.setFillColor(43, 43, 43);
    doc.rect(0, 0, 148, 24, "F");
    doc.setTextColor(255, 201, 60);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Pop Panda", 12, 15);
    doc.setFontSize(9);
    doc.setTextColor(255, 248, 240);
    doc.text("Happiness on a stick", 12, 20);
    doc.setFontSize(9);
    doc.text("Digital Receipt", 148 - 12, 15, { align: "right" });

    doc.setTextColor(43, 43, 43);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(payment.receipt_number || paymentId, 12, 34);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Date: ${paidAt.toLocaleString()}`, 12, 40);
    doc.text(`Txn ID: ${payment.razorpay_payment_id || "—"}`, 12, 45);
    doc.text(`Outlet: ${payment.outlet_name || "Pop Panda Go"}`, 12, 50);
    if (payment.customer_name) doc.text(`Customer: ${payment.customer_name}`, 12, 55);
    if (member?.member_code || payment.member_code) doc.text(`Member: ${member?.member_code || payment.member_code}`, 12, 60);

    // Divider
    doc.setDrawColor(230, 221, 211);
    doc.line(12, 66, 136, 66);

    // Amount
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text("Amount Paid", 12, 75);
    doc.setFontSize(22);
    doc.setTextColor(43, 43, 43);
    doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${Number(payment.amount).toFixed(2)}`, 12, 85);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(21, 128, 61);
    doc.text("● Payment Successful", 12, 92);
    doc.setTextColor(43, 43, 43);
    doc.setFontSize(9);
    doc.text("Method: Razorpay (UPI/Card/Netbanking)", 12, 97);

    // Scratch card summary
    if (scratchCard) {
      doc.line(12, 103, 136, 103);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Scratch Card", 12, 110);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const snap = scratchCard.reward_snapshot || {};
      doc.text(`Result: ${snap.is_win ? snap.name : "Better luck next time"}`, 12, 116);
      if (snap.is_win && scratchCard.saved_at) doc.text("Saved to Panda Club: Yes", 12, 121);
    }

    // ---- QR code (member profile) ----
    // Encode member code (or receipt as fallback) so the admin scanner can
    // instantly open the customer's Panda Club profile.
    try {
      const qrCanvas = document.getElementById("receipt-qr-canvas");
      if (qrCanvas && qrCanvas.tagName === "CANVAS") {
        const dataUrl = qrCanvas.toDataURL("image/png");
        doc.addImage(dataUrl, "PNG", 148 - 12 - 28, 32, 28, 28);
        doc.setFontSize(7);
        doc.setTextColor(90, 90, 90);
        doc.text("Scan at counter", 148 - 12 - 14, 63, { align: "center" });
      }
    } catch { /* ignore */ }

    // ---- Contact & address block ----
    doc.setDrawColor(230, 221, 211);
    doc.line(12, 130, 136, 130);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(43, 43, 43);
    doc.text("Retail Store", 12, 137);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(BRAND.retail, 12, 142);
    (BRAND.addresses?.retail?.lines || []).slice(0, 3).forEach((ln, i) => {
      doc.text(ln, 12, 146 + i * 4);
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(43, 43, 43);
    doc.text("Manufactured & Marketed by", 82, 137);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(BRAND.legal, 82, 142);
    (BRAND.addresses?.manufacturing?.lines || []).slice(0, 3).forEach((ln, i) => {
      doc.text(ln, 82, 146 + i * 4);
    });

    // Footer
    doc.setDrawColor(230, 221, 211);
    doc.line(12, 175, 136, 175);
    doc.setFontSize(8);
    doc.setTextColor(43, 43, 43);
    doc.text("Thank you for choosing Pop Panda!", 12, 181);
    doc.setTextColor(90, 90, 90);
    doc.text(`Support: hello@thepoppanda.com  |  WhatsApp: +${BRAND.whatsappNumber}`, 12, 186);
    doc.text("www.thepoppanda.com  |  Generated digitally by The Pop Panda", 12, 191);
    return doc;
  };

  const downloadPdf = () => {
    const doc = buildPdf();
    if (!doc) return;
    doc.save(`PopPanda-Receipt-${payment.receipt_number || paymentId}.pdf`);
  };

  const printReceipt = () => window.print();

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Pop Panda Receipt ${payment.receipt_number}`,
          text: `Payment of ₹${Number(payment.amount).toFixed(2)} at ${payment.outlet_name}.`,
          url: window.location.href,
        });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Receipt link copied");
      } catch {
        toast.error("Unable to share");
      }
    }
  };

  if (err) return (
    <div className="min-h-screen bg-[#FFF8F0] grid place-items-center px-6 text-center text-brand-charcoal">
      <div>
        <div className="text-brand-red uppercase tracking-[0.28em] text-xs mb-2">Receipt unavailable</div>
        <div className="font-display text-2xl">{err}</div>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-brand-charcoal/70 hover:text-brand-charcoal"><ArrowLeft size={14} /> Back home</Link>
      </div>
    </div>
  );
  if (!payment) return <div className="min-h-screen bg-[#FFF8F0] grid place-items-center">Loading receipt…</div>;

  const paidAt = payment.paid_at ? new Date(payment.paid_at) : null;

  return (
    <div className="min-h-screen bg-[#FFF8F0] text-brand-charcoal print:bg-white" data-testid="receipt-page">
      <header className="max-w-2xl mx-auto flex items-center justify-between px-5 py-5 print:hidden">
        <Link to={member ? "/member/dashboard" : "/"} className="inline-flex items-center gap-2 text-sm text-brand-charcoal/70 hover:text-brand-charcoal">
          <ArrowLeft size={16} /> Back
        </Link>
        <div className="flex gap-2">
          <button onClick={share} data-testid="receipt-share" className="btn-pill btn-ghost text-xs"><Share2 size={12} /> Share</button>
          <button onClick={printReceipt} data-testid="receipt-print" className="btn-pill btn-ghost text-xs"><Printer size={12} /> Print</button>
          <button onClick={downloadPdf} data-testid="receipt-download" className="btn-pill btn-dark text-xs"><Download size={12} /> PDF</button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-12">
        <motion.article
          ref={receiptRef}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="rounded-3xl bg-white border border-brand-line shadow-lg overflow-hidden print:shadow-none print:border-0"
          data-testid="receipt-card"
        >
          {/* Branded header */}
          <div className="bg-brand-charcoal text-brand-cream px-8 py-8 relative">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src={BRAND.logoUrl} alt="Pop Panda" className="h-11 w-11 bg-brand-cream rounded-full p-1.5" />
                <div>
                  <div className="font-display text-2xl leading-none">Pop Panda</div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-brand-yellow mt-1">Happiness on a stick</div>
                </div>
              </div>
              <div className="text-right">
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-2.5 py-1"><ShieldCheck size={11} /> Paid</div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-brand-cream/60">Digital Receipt</div>
              </div>
            </div>
          </div>

          {/* Meta */}
          <div className="px-8 pt-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Receipt No.</div>
                <div className="font-mono text-lg mt-0.5" data-testid="receipt-number">{payment.receipt_number || paymentId}</div>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-brand-charcoal/55">Transaction ID</div>
                <div className="font-mono text-xs mt-0.5 break-all" data-testid="receipt-txn-id">{payment.razorpay_payment_id || "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Paid on</div>
                <div className="text-sm mt-0.5">{paidAt ? paidAt.toLocaleString() : "—"}</div>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-brand-charcoal/55">Outlet</div>
                <div className="text-sm mt-0.5" data-testid="receipt-outlet">{payment.outlet_name || "Pop Panda Go"}</div>
              </div>
            </div>
          </div>

          {/* Amount block */}
          <div className="mx-8 mt-6 rounded-2xl bg-gradient-to-br from-brand-yellow/20 via-brand-yellow/8 to-transparent border border-brand-yellow/30 p-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brand-charcoal/60">Amount Paid</div>
            <div className="font-display text-5xl mt-1 text-brand-charcoal" data-testid="receipt-amount">₹{Number(payment.amount).toFixed(2)}</div>
            <div className="mt-2 text-sm text-emerald-700 inline-flex items-center gap-1.5"><CheckCircle2 size={14} /> Payment Successful</div>
            <div className="mt-1 text-xs text-brand-charcoal/60">via Razorpay (UPI / Cards / Netbanking)</div>
          </div>

          {/* Customer info */}
          {(payment.customer_name || payment.customer_phone || member) && (
            <div className="px-8 mt-6 grid sm:grid-cols-2 gap-4">
              {(payment.customer_name || member?.name) && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Customer</div>
                  <div className="text-sm mt-0.5">{payment.customer_name || member?.name}</div>
                </div>
              )}
              {(payment.customer_phone || member?.phone) && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Phone</div>
                  <div className="text-sm mt-0.5">{payment.customer_phone || member?.phone}</div>
                </div>
              )}
              {(payment.member_code || member?.member_code) && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Panda Club ID</div>
                  <div className="text-sm mt-0.5 font-mono">{payment.member_code || member?.member_code}</div>
                </div>
              )}
            </div>
          )}

          {/* Scratch card block */}
          {scratchCard && (
            <div className="mx-8 mt-6 rounded-2xl border border-brand-line p-5 bg-brand-muted/30" data-testid="receipt-scratch-block">
              <div className="text-[11px] uppercase tracking-[0.24em] text-brand-red">Scratch Card</div>
              <div className="mt-1 flex items-center justify-between gap-3">
                <div>
                  <div className="font-display text-lg">
                    {scratchCard.reward_snapshot?.is_win ? scratchCard.reward_snapshot?.name : "Better luck next time"}
                  </div>
                  {scratchCard.reward_snapshot?.description && (
                    <div className="text-xs text-brand-charcoal/60 mt-0.5">{scratchCard.reward_snapshot.description}</div>
                  )}
                </div>
                {scratchCard.saved_at ? (
                  <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Saved</span>
                ) : scratchCard.revealed_at ? (
                  <span className="text-[10px] uppercase tracking-widest bg-brand-yellow/25 text-brand-charcoal px-2 py-0.5 rounded-full">Revealed</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Unscratched</span>
                )}
              </div>
            </div>
          )}

          {/* Brand + QR footer */}
          <div className="mt-6 mx-8 rounded-2xl border border-brand-line bg-brand-muted/20 p-5" data-testid="receipt-brand-block">
            <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-start">
              <div className="grid sm:grid-cols-2 gap-5 text-xs text-brand-charcoal/80 flex-1">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-brand-charcoal/55 inline-flex items-center gap-1"><MapPin size={10} /> Retail Store</div>
                  <div className="font-medium mt-0.5">{BRAND.retail}</div>
                  {(BRAND.addresses?.retail?.lines || []).map((ln, i) => <div key={i} className="text-brand-charcoal/70">{ln}</div>)}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-brand-charcoal/55">Manufactured & Marketed by</div>
                  <div className="font-medium mt-0.5">{BRAND.legal}</div>
                  {(BRAND.addresses?.manufacturing?.lines || []).map((ln, i) => <div key={i} className="text-brand-charcoal/70">{ln}</div>)}
                </div>
                <div className="sm:col-span-2 flex flex-wrap gap-4 text-[11px]">
                  <a href={`https://wa.me/${BRAND.whatsappNumber}`} className="inline-flex items-center gap-1.5 text-brand-charcoal hover:text-brand-red"><Phone size={11} /> +{BRAND.whatsappNumber}</a>
                  <a href="mailto:hello@thepoppanda.com" className="inline-flex items-center gap-1.5 text-brand-charcoal hover:text-brand-red"><Mail size={11} /> hello@thepoppanda.com</a>
                  <a href="https://www.thepoppanda.com" className="inline-flex items-center gap-1.5 text-brand-charcoal hover:text-brand-red"><Globe size={11} /> www.thepoppanda.com</a>
                </div>
              </div>
              {/* QR code — links to member profile (admin scanner recognises it) */}
              <div className="text-center print:block" data-testid="receipt-qr-block">
                <div className="p-2 bg-white border border-brand-line rounded-xl inline-block">
                  <QRCodeCanvas
                    id="receipt-qr-canvas"
                    value={member?.member_code || payment.member_code || payment.receipt_number || paymentId}
                    size={96}
                    level="M"
                    marginSize={0}
                    fgColor="#2B2B2B"
                    bgColor="#ffffff"
                  />
                </div>
                <div className="text-[9px] uppercase tracking-[0.22em] mt-1 text-brand-charcoal/55">
                  {member?.member_code || payment.member_code ? "Panda Club ID" : "Receipt QR"}
                </div>
                <div className="text-[10px] font-mono mt-0.5 text-brand-charcoal">{member?.member_code || payment.member_code || (payment.receipt_number || paymentId).slice(0, 12)}</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 px-8 pb-8 border-t border-brand-line pt-5">
            <div className="text-center text-sm text-brand-charcoal/70">Thank you for choosing Pop Panda!</div>
            <div className="mt-2 text-center text-[10px] uppercase tracking-widest text-brand-charcoal/45">Generated digitally by The Pop Panda</div>
            <div className="mt-3 flex items-center justify-center text-[10px] uppercase tracking-widest text-brand-charcoal/50">
              <div className="inline-flex items-center gap-1.5"><ReceiptIcon size={11} /> Keep this receipt for your records</div>
            </div>
          </div>
        </motion.article>

        {!member && (
          <div className="mt-6 rounded-2xl border border-brand-yellow/40 bg-brand-yellow/10 p-5 text-center print:hidden">
            <div className="text-[10px] uppercase tracking-[0.28em] text-brand-charcoal/60">Save your rewards for later</div>
            <div className="font-display text-xl mt-1">Join Panda Club — Free</div>
            <div className="mt-3 flex gap-2 justify-center">
              <Link to="/member/register" className="btn-pill btn-dark text-xs">Register</Link>
              <Link to="/member/login" className="btn-pill btn-outline-dark text-xs">Sign in</Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
